/**
 * Navigation Service
 * Handles navigation-related API calls (user context, sidebar menu)
 */

import api from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api.constants'
import { UserContext } from '@/types/shared/auth.types'
import { MenuItem } from '@/types/shared/common.types'
import { getTenantStorageKey, getTenantFromHostname } from '@/utils/tenant.util'
import { setUserContextCookie } from '@/utils/user-context-cookie.util'

export type NavigationRouteCatalogEntry = {
  route: string
  isActive: boolean
  isVisible: boolean
}

export interface NavigationRegistryEntry {
  mainMenuId?: string
  mainMenuName?: string
  scope?: string
  platformKey?: string
  route?: string
  orderIndex?: number
  icon?: string | null
  scopeType?: 'admin' | 'user'
  /** Server navigation UX audiences (persona surfacing); replaces legacy scopeType when present. */
  navigationAudiences?: string[]
  children?: Array<{
    subMenuId?: string
    name?: string
    permission?: string
    anyOfPermissions?: string[]
    feature?: string | null
    guideUrl?: string | null
    route?: string
    orderIndex?: number
    icon?: string | null
    scopeType?: 'admin' | 'user'
    navigationAudiences?: string[]
  }>
}

/**
 * User Context Response
 */
export interface UserContextResponse {
  user: {
    id: string
    email: string
    name: string
    userType: string
  }
  domainNavigation: {
    /** Backend includes organizationId, branchId, organizationName, branchName, contextType for navbar/buildContextTree */
    accessibleDomains: Array<{
      type: string
      name: string
      organizationSlug?: string
      branchSlug?: string
      organizationId?: string
      branchId?: string
      organizationName?: string
      branchName?: string
      contextType?: 'organization' | 'branch'
      id?: string
    }>
  }
}

/**
 * Sidebar Menu Response
 */
export interface SidebarMenuResponse {
  sidebarMenu: MenuItem[]
  quickDrawer?: MenuItem[]
  dashboardPriority?: boolean
  homeRedirectUrl?: string
  userContext?: {
    userType: string
    organizationSlug?: string
    branchSlug?: string
    isOrgAdmin?: boolean
    designation?: string | null
  }
  roleAssignmentMessage?: string
}

class NavigationService {
  private userContextInFlight?: Promise<UserContextResponse>
  private lastUserContextErrorAt: number | null = null
  /**
   * Get user context (accessible domains/contexts)
   * Uses access token (from login)
   * GET /api/navigation/user-context
   *
   * Not cached: previous cache key did not bind to user id, which allowed cross-account leakage after login-without-hard-refresh.
   */
  async getUserContext(forceRefresh = false): Promise<UserContextResponse> {
    const now = Date.now()

    if (!forceRefresh) {
      if (this.lastUserContextErrorAt && now - this.lastUserContextErrorAt < 5000) {
        throw new Error('User context recently failed; skipping retry')
      }
      if (this.userContextInFlight) {
        return this.userContextInFlight
      }
    } else if (this.userContextInFlight) {
      await this.userContextInFlight.catch(() => undefined)
    }

    this.userContextInFlight = (async () => {
      const response = await api.get(API_ENDPOINTS.NAVIGATION.USER_CONTEXT)
      return response.data.data as UserContextResponse
    })()

    try {
      const data = await this.userContextInFlight
      setUserContextCookie(data)
      this.lastUserContextErrorAt = null
      return data
    } catch (error) {
      this.lastUserContextErrorAt = Date.now()
      throw error
    } finally {
      this.userContextInFlight = undefined
    }
  }

  /**
   * Get sidebar menu storage key (tenant-aware)
   */
  private getSidebarMenuStorageKey(): string {
    return getTenantStorageKey('sidebarMenu', getTenantFromHostname())
  }

  /**
   * Get sidebar menu from localStorage only (tenant-aware)
   * NOTE: Cookies check removed to avoid "Request Header Or Cookie Too Large" errors
   */
  getStoredSidebarMenu(): SidebarMenuResponse | null {
    try {
      // Check tenant-specific localStorage only
      const storageKey = this.getSidebarMenuStorageKey()
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Check if stored data is still valid (has menu items)
        if (parsed && parsed.sidebarMenu && Array.isArray(parsed.sidebarMenu)) {
          return parsed
        }
      }
    } catch (error) {
    }
    return null
  }

  /**
   * Store sidebar menu in localStorage only (tenant-aware)
   * NOTE: NOT stored in cookies to avoid "Request Header Or Cookie Too Large" errors
   * Sidebar menu can be large and is only needed client-side, so localStorage is sufficient
   * Cross-tab sync can be handled via localStorage events if needed
   */
  storeSidebarMenu(menuData: SidebarMenuResponse): void {
    try {
      const storageKey = this.getSidebarMenuStorageKey()
      const menuDataStr = JSON.stringify(menuData)
      
      // Store in localStorage only (tenant-aware)
      // Do NOT store in cookies - sidebar menu can be large and causes header size issues
      localStorage.setItem(storageKey, menuDataStr)
    } catch (error) {
    }
  }

  /**
   * Clear stored sidebar menu from localStorage only
   * NOTE: Cookies removal removed as sidebar menu is no longer stored in cookies
   */
  clearStoredSidebarMenu(): void {
    try {
      const storageKey = this.getSidebarMenuStorageKey()
      localStorage.removeItem(storageKey)
      // Also remove legacy global key if exists
      localStorage.removeItem('sidebarMenu')
    } catch (error) {
    }
  }

  /**
   * Get sidebar menu based on current context
   * Uses context token (from select-context)
   * GET /api/navigation/sidebar
   * 
   * Always fetches from API; localStorage is used only as a cache hint elsewhere.
   * 
   * @param forceRefresh - Force fetch from API (kept for API compatibility)
   * @param contextToken - Optional context token to use (for login flow)
   * @param skipStorage - If true, don't store the menu (for login flow)
   */
  async getSidebarMenu(contextToken?: string, skipStorage = false): Promise<SidebarMenuResponse> {
    // If contextToken is provided, use it directly; otherwise rely on axios interceptor
    const config = contextToken 
      ? { headers: { Authorization: `Bearer ${contextToken}` } }
      : {}
    
    const response = await api.get(API_ENDPOINTS.NAVIGATION.SIDEBAR, config)
    const menuData = response.data.data
    
    // Store in localStorage only if not skipping storage
    if (!skipStorage) {
    this.storeSidebarMenu(menuData)
    } else {
    }
    
    return menuData
  }

  /**
   * Store sidebar menu after navigation completes
   * This is called after navigation to store the menu that was kept in memory during login flow
   */
  storeSidebarMenuAfterNavigation(menuData: SidebarMenuResponse): void {
    this.storeSidebarMenu(menuData)
  }

  /**
   * Get navigation registry for a scope (structure + guideUrl per feature).
   * Used at runtime so guide URLs update when features are edited without running generate:navigation.
   * GET /api/navigation/registry?scope=platform|organization|branch
   */
  async getRegistry(scope: 'platform' | 'organization' | 'branch'): Promise<NavigationRegistryEntry[]> {
    const response = await api.get<{ data?: NavigationRegistryEntry[] }>(
      `${API_ENDPOINTS.NAVIGATION.REGISTRY}?scope=${scope}`
    )
    const data = response.data?.data ?? response.data
    return Array.isArray(data) ? data : []
  }

  async getRouteCatalog(
    scope: 'platform' | 'organization' | 'branch'
  ): Promise<NavigationRouteCatalogEntry[]> {
    const response = await api.get<{ data?: NavigationRouteCatalogEntry[] }>(
      `${API_ENDPOINTS.NAVIGATION.ROUTE_CATALOG}?scope=${scope}`
    )
    const data = response.data?.data ?? response.data
    return Array.isArray(data) ? data : []
  }

  /**
   * Transform accessible domains to UserContext format
   * Backend returns lowercase types: 'platform', 'organization', 'branch'
   * Frontend uses uppercase: 'PLATFORM', 'ORGANIZATION', 'BRANCH'
   */
  transformDomainsToContexts(domains: UserContextResponse['domainNavigation']['accessibleDomains']): UserContext[] {
    return domains.map((domain, index) => {
      // Convert backend lowercase type to frontend uppercase type
      const typeMap: Record<string, UserContext['type']> = {
        'platform': 'PLATFORM',
        'organization': 'ORGANIZATION',
        'branch': 'BRANCH',
      }
      
      // Parse organization and branch names from domain.name
      let orgName: string | undefined
      let branchName: string | undefined
      
      if (domain.type === 'organization') {
        // For organization: "Qubicgen schools Management" -> orgName
        orgName = domain.name
      } else if (domain.type === 'branch') {
        // For branch: "Qubicgen Main Branch - Qubicgen schools" -> branchName and orgName
        const parts = domain.name.split(' - ')
        if (parts.length >= 2) {
          branchName = parts[0] // "Qubicgen Main Branch"
          orgName = parts[1] // "Qubicgen schools"
        } else {
          // Fallback if format is different
          branchName = domain.name
        }
      } else if (domain.type === 'platform') {
        orgName = undefined
        branchName = undefined
      }
      
      return {
        id: domain.id || `context-${index}`,
        type: typeMap[domain.type] || 'PLATFORM',
        organizationSlug: domain.organizationSlug,
        branchSlug: domain.branchSlug,
        // Set both orgSlug/orgName and organizationSlug/organizationName for compatibility
        orgSlug: domain.organizationSlug,
        orgName: orgName,
        branchName: branchName,
        organizationName: orgName,
      }
    })
  }
}

export const navigationService = new NavigationService()
export default navigationService

